// Quality Management page and its dialogs. Values written to records (group /
// role names, CAPA type-status, notification type-priority, policy category)
// stay Turkish; the profile matches on them. Only on-screen texts live here.
const quality = {
  page: {
    title: 'Quality Management',
    subtitle: 'Procedures, policies, forms, trainings and quality records in one place.',
    open: 'Open',
    help: {
      profile: 'SOPs, policies, job descriptions, forms and indication trainings are assigned to the departments you choose; employees in those departments see them under Profile › Files. Choosing “All” makes them visible to everyone.',
      care: 'Anything you add, edit or delete here shows up on the employees’ profiles right away. Check the groups before saving so nothing goes to the wrong department.',
    },
    badges: {
      profile: 'Shown on profiles',
      quiz: 'Feeds profile quizzes',
    },
    sections: {
      documents: {
        title: 'Document Library',
        text: 'Documents assigned to departments and listed on the employees’ profiles.',
      },
      training: {
        title: 'Training and Quizzes',
        text: 'Video trainings, end-of-reading quiz questions and assignment tracking.',
      },
      records: {
        title: 'Quality Records',
        text: 'Corrective / preventive actions and quality notifications. These records are kept here only and are not shown on profiles.',
      },
    },
    modules: {
      sop: { title: 'SOP', text: 'Standard operating procedures; track read confirmations and readers.' },
      policy: { title: 'Policies', text: 'Company policies and guidelines with category and version.' },
      jobDescription: { title: 'Job Descriptions', text: 'Role and responsibility definitions for each position.' },
      forms: { title: 'Forms', text: 'Standard form and document templates; open or download in one click.' },
      education: { title: 'Indication Training Module', text: 'Create video trainings with chapters and quizzes.' },
      questionPool: { title: 'Question Pool', text: 'Department-based end-of-reading quiz questions for SOPs and trainings.' },
      assignments: { title: 'Assignments', text: 'See SOP and training assignments, due dates and completion.' },
      capa: { title: 'CAPA', text: 'Record and track corrective and preventive actions.' },
      notification: { title: 'Notifications', text: 'System, quality and security notifications with priority and due date.' },
    },
  },

  common: {
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    adding: 'Adding...',
    updating: 'Updating...',
    edit: 'Edit',
    delete: 'Delete',
    deleteConfirm: 'Yes, delete',
    deleteMessage: '“{{name}}” will be deleted permanently. This cannot be undone.',
    loading: 'Loading...',
    choose: 'Select',
    na: 'Not specified',
    notAssigned: 'Not assigned',
    openFile: 'Open file',
    groups: 'Assign to groups',
    groupsHint: 'Employees in the selected departments see this record on their profile. “All” means every employee.',
    audience: 'Related staff',
    audienceHint: 'Optional · for information only, it does not change who sees it on profiles.',
  },

  groupsPicker: {
    selected_one: '{{count}} selected',
    selected_other: '{{count}} selected',
  },

  groups: {
    all: 'All',
    siteCoordinator: 'Site Coordinator',
    researchNurse: 'Clinical Research Nurse',
    projectManager: 'Project Manager',
    startupLead: 'Study Start-up Lead',
    backup: 'Backup Staff',
    cra: 'CRA',
    qualityManager: 'Quality Manager',
    finance: 'Finance Specialist',
    it: 'IT Specialist',
    hr: 'HR Specialist',
    bizDev: 'Business Development Specialist',
    training: 'Training Lead',
  },

  file: {
    choose: 'No file selected',
    dropHint: 'Choose a file or drag it here',
    pick: 'Choose file',
    change: 'Change',
    current: 'No new file selected',
    viewCurrent: 'View current file',
  },

  validation: {
    sopName: 'Please enter the SOP name',
    sopFileType: 'You can only upload PDF, MP4 and PowerPoint (.ppt, .pptx) files',
    dueDate: 'Please enter the due date',
    approvalDate: 'Please enter the approval date',
    version: 'Please enter the version number',
    category: 'Please select a category',
    policyName: 'Please enter the policy name',
    file: 'Please upload a file',
    group: 'Please select at least one group',
    capaTitle: 'Please enter the CAPA title',
    capaType: 'Please select the CAPA type',
    capaStatus: 'Please select the CAPA status',
    createdDate: 'Please enter the creation date',
    notificationTitle: 'Please enter the notification title',
    notificationType: 'Please select the notification type',
    priority: 'Please select the priority',
    date: 'Please enter the date',
    jobTitle: 'Please enter the job description title',
    position: 'Please enter the position',
    educationTitle: 'Please enter the training title',
    educationDescription: 'Please enter the training description',
    video: 'Please select a video file',
    chapterTitles: 'Every chapter needs a title',
    popQuestions: 'Fill in all pop quiz questions',
    popOptions: 'Fill in all pop quiz options',
    finalQuestions: 'Fill in all final quiz questions',
    finalOptions: 'Fill in all final quiz options',
    targetGroup: 'Select at least one target group',
  },

  sop: {
    title: 'SOP Management',
    subtitle_one: '{{count}} SOP · by department folder',
    subtitle_other: '{{count}} SOPs · by department folder',
    help: 'The folders are departments; an SOP can be assigned to several of them. Use the people icon to see who has read an SOP.',
    add: 'New SOP',
    search: 'Search SOP name or version...',
    folders: 'Department folders',
    all: 'All SOPs',
    ungrouped: 'Ungrouped',
    count_one: '{{count}} SOP',
    count_other: '{{count}} SOPs',
    emptyFolder: 'No SOPs in this folder',
    noMatch: 'No SOPs match your search',
    readers: 'See readers',
    deleteTitle: 'Delete SOP?',
    addTitle: 'New SOP',
    editTitle: 'Edit SOP',
    formSubtitle: 'Standard operating procedure',
    formHelp: 'Employees in the selected groups see the SOP on their profile and confirm reading it. The due date is shown there as a reminder.',
    columns: {
      name: 'SOP Name',
      version: 'Version',
      approvalDate: 'Approval Date',
      dueDate: 'Due Date',
    },
    fields: {
      name: 'SOP name',
      namePlaceholder: 'e.g. Informed Consent Process',
      version: 'Version number',
      versionPlaceholder: 'e.g. 2.0',
      approvalDate: 'Approval date',
      dueDate: 'Due date',
      dueDateHint: 'Employees are expected to read the SOP by this date.',
      file: 'SOP file',
      fileHint: 'PDF, MP4 or PowerPoint (.ppt, .pptx)',
    },
    toast: {
      loadError: 'Could not load SOPs',
      added: 'SOP added',
      addError: 'Could not add the SOP',
      updated: 'SOP updated',
      updateError: 'Could not update the SOP',
      deleted: 'SOP deleted',
      deleteError: 'Could not delete the SOP',
    },
  },

  readers: {
    title: 'SOP Readers',
    sopName: 'SOP',
    version: 'Version',
    groups: 'Assigned groups',
    stats: {
      readers: 'Readers',
      employees: 'Total employees',
      rate: 'Read rate',
    },
    help: 'Employees who confirmed reading are listed. The rate is calculated against all employees in the system.',
    empty: 'No one has confirmed reading yet',
    read: 'Read',
    titleLabel: 'Title',
    department: 'Department',
    readAt: 'Read on',
    unknown: 'Unknown user',
    toast: {
      loadError: 'Could not load readers',
    },
  },

  policy: {
    title: 'Policies',
    subtitle_one: '{{count}} policy',
    subtitle_other: '{{count}} policies',
    help: 'Policies are assigned to the selected departments and shown on the employees’ profiles. Use the row icons to open the file, edit or delete a record.',
    add: 'New Policy',
    search: 'Search policy name, category or version...',
    empty: 'No policies yet',
    noMatch: 'No policies match your search',
    deleteTitle: 'Delete policy?',
    addTitle: 'New Policy',
    editTitle: 'Edit Policy',
    formSubtitle: 'Company policy and guideline',
    formHelp: 'Employees in the selected groups see the policy on their profile.',
    categories: {
      quality: 'Quality',
      hr: 'Human Resources',
      infoSec: 'Information Security',
      health: 'Health and Safety',
      environment: 'Environment',
      general: 'General',
    },
    columns: {
      name: 'Policy Name',
      category: 'Category',
      version: 'Version',
      approvalDate: 'Approval Date',
      groups: 'Groups',
    },
    fields: {
      name: 'Policy name',
      namePlaceholder: 'e.g. Information Security Policy',
      category: 'Category',
      version: 'Version number',
      versionPlaceholder: 'e.g. 1.0',
      approvalDate: 'Approval date',
      description: 'Description',
      descriptionPlaceholder: 'Briefly describe the scope of the policy',
      file: 'Policy file',
      fileHint: 'PDF or Word (.doc, .docx)',
    },
    toast: {
      loadError: 'Could not load policies',
      added: 'Policy added',
      addError: 'Could not add the policy',
      updated: 'Policy updated',
      updateError: 'Could not update the policy',
      deleted: 'Policy deleted',
      deleteError: 'Could not delete the policy',
    },
  },

  jobDescription: {
    title: 'Job Descriptions',
    subtitle_one: '{{count}} job description',
    subtitle_other: '{{count}} job descriptions',
    help: 'Job descriptions are assigned to the selected departments and shown on those employees’ profiles.',
    add: 'New Job Description',
    search: 'Search title or position...',
    empty: 'No job descriptions yet',
    noMatch: 'No job descriptions match your search',
    deleteTitle: 'Delete job description?',
    addTitle: 'New Job Description',
    editTitle: 'Edit Job Description',
    formSubtitle: 'Role and responsibilities of the position',
    formHelp: 'Employees in the selected groups see the job description on their profile.',
    columns: {
      title: 'Title',
      position: 'Position',
      createdDate: 'Created',
      groups: 'Groups',
    },
    fields: {
      title: 'Title',
      titlePlaceholder: 'e.g. Site Coordinator Job Description',
      position: 'Position',
      positionPlaceholder: 'e.g. Site Coordinator',
      createdDate: 'Creation date',
      description: 'Description',
      descriptionPlaceholder: 'Briefly list the key responsibilities of the role',
      file: 'Job description file',
      fileHint: 'PDF or Word (.doc, .docx)',
    },
    toast: {
      loadError: 'Could not load job descriptions',
      added: 'Job description added',
      addError: 'Could not add the job description',
      updated: 'Job description updated',
      updateError: 'Could not update the job description',
      deleted: 'Job description deleted',
      deleteError: 'Could not delete the job description',
    },
  },

  forms: {
    title: 'Forms',
    subtitle_one: '{{count}} form',
    subtitle_other: '{{count}} forms',
    help: 'Click a row to open the form file in a new tab; use the download icon to save it. Forms are shown on the profiles of the selected departments.',
    add: 'New Form',
    search: 'Search form name, category or group...',
    empty: 'No forms yet',
    noMatch: 'No forms match your search',
    rowOpen: 'Open the form in a new tab',
    rowNoFile: 'This form has no file',
    download: 'Download file',
    deleteTitle: 'Delete form?',
    addTitle: 'New Form',
    editTitle: 'Edit Form',
    formSubtitle: 'Standard form and document template',
    formHelp: 'Employees in the selected groups see the form on their profile and can download it.',
    groupsLocked: 'Assigned groups can’t be changed in this window. To change them, delete the form and add it again with the right groups.',
    columns: {
      name: 'Form Name',
      category: 'Category',
      version: 'Version',
      approvalDate: 'Approval Date',
      groups: 'Groups',
    },
    fields: {
      name: 'Form name',
      namePlaceholder: 'e.g. Adverse Event Report Form',
      category: 'Category',
      categoryPlaceholder: 'e.g. Clinical',
      version: 'Version',
      versionPlaceholder: 'e.g. 1.0',
      uploadDate: 'Upload date',
      file: 'Form file',
      fileHint: 'PDF, Word or Excel',
    },
    toast: {
      loadError: 'Could not load forms',
      added: 'Form added',
      addError: 'Could not add the form',
      updated: 'Form updated',
      updateError: 'Could not update the form',
      deleted: 'Form deleted',
      deleteError: 'Could not delete the form',
      downloading: 'Downloading file...',
      noFile: 'File not found',
      formHasNoFile: 'This form has no file',
    },
  },

  capa: {
    title: 'CAPA',
    subtitle_one: '{{count}} corrective / preventive action',
    subtitle_other: '{{count}} corrective / preventive actions',
    help: 'Track corrective and preventive actions by status. Set completed records to “Closed”. CAPA records are not shown on profiles.',
    add: 'New CAPA',
    search: 'Search title, type or status...',
    empty: 'No CAPA records yet',
    noMatch: 'No CAPA records match your search',
    deleteTitle: 'Delete CAPA?',
    addTitle: 'New CAPA',
    editTitle: 'Edit CAPA',
    formSubtitle: 'Corrective or preventive action',
    formHelp: 'Choose the type and status; the due date is when the action is expected to be closed. Attaching a file is optional.',
    types: {
      corrective: 'Corrective Action',
      preventive: 'Preventive Action',
    },
    statuses: {
      open: 'Open',
      ongoing: 'In Progress',
      closed: 'Closed',
    },
    columns: {
      name: 'Title',
      type: 'Type',
      status: 'Status',
      createdDate: 'Created',
      dueDate: 'Due Date',
    },
    fields: {
      name: 'Title',
      namePlaceholder: 'e.g. Missing source document',
      type: 'Type',
      status: 'Status',
      createdDate: 'Creation date',
      dueDate: 'Due date',
      dueDateHint: 'When the action is expected to be closed.',
      description: 'Description',
      descriptionPlaceholder: 'Briefly describe the issue and the action to take',
      file: 'Attachment',
      fileHint: 'Optional · PDF, Word or Excel',
    },
    toast: {
      loadError: 'Could not load CAPA records',
      added: 'CAPA added',
      addError: 'Could not add the CAPA',
      updated: 'CAPA updated',
      updateError: 'Could not update the CAPA',
      deleted: 'CAPA deleted',
      deleteError: 'Could not delete the CAPA',
    },
  },

  notification: {
    title: 'Notifications',
    subtitle_one: '{{count}} notification',
    subtitle_other: '{{count}} notifications',
    help: 'Record system, quality, security and general notifications with priority and due date. These records are not shown on profiles.',
    add: 'New Notification',
    search: 'Search title, type or priority...',
    empty: 'No notifications yet',
    noMatch: 'No notifications match your search',
    deleteTitle: 'Delete notification?',
    addTitle: 'New Notification',
    editTitle: 'Edit Notification',
    formSubtitle: 'Quality notification',
    formHelp: 'Choose the type and priority. The due date is when the related work is expected to be done.',
    types: {
      system: 'System',
      quality: 'Quality',
      security: 'Security',
      general: 'General',
    },
    priorities: {
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
    columns: {
      title: 'Title',
      type: 'Type',
      priority: 'Priority',
      date: 'Date',
      dueDate: 'Due Date',
    },
    fields: {
      title: 'Title',
      titlePlaceholder: 'e.g. System maintenance',
      type: 'Type',
      priority: 'Priority',
      date: 'Date',
      dueDate: 'Due date',
      dueDateHint: 'When the related work is expected to be done.',
      description: 'Description',
      descriptionPlaceholder: 'Write the details of the notification',
      file: 'Attachment',
      fileHint: 'Optional · PDF, Word or Excel',
    },
    toast: {
      loadError: 'Could not load notifications',
      added: 'Notification added',
      addError: 'Could not add the notification',
      updated: 'Notification updated',
      updateError: 'Could not update the notification',
      deleted: 'Notification deleted',
      deleteError: 'Could not delete the notification',
    },
  },

  education: {
    title: 'Indication Training Module',
    subtitle_one: '{{count}} training',
    subtitle_other: '{{count}} trainings',
    help: 'Trainings appear on the profiles of employees in the target groups; they watch the video and take the pop quizzes and the final quiz. Trainings past their access deadline are marked “Expired”.',
    add: 'New Training',
    search: 'Search training or instructor...',
    empty: 'No trainings yet',
    noMatch: 'No trainings match your search',
    watchVideo: 'Watch video',
    chapterCount_one: '{{count}} chapter',
    chapterCount_other: '{{count}} chapters',
    popCount_one: '{{count}} pop quiz',
    popCount_other: '{{count}} pop quizzes',
    finalCount_one: '{{count}} final question',
    finalCount_other: '{{count}} final questions',
    expired: 'Expired',
    deleteTitle: 'Delete training?',
    difficulty: {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced',
    },
    columns: {
      title: 'Training',
      content: 'Content',
      duration: 'Video length',
      difficulty: 'Level',
      groups: 'Target Groups',
      dueDate: 'Due Date',
      deadline: 'Access Deadline',
    },
    toast: {
      loadError: 'Could not load trainings',
      deleted: 'Training deleted',
      deleteError: 'Could not delete the training',
    },
  },

  educationForm: {
    addTitle: 'New Training',
    addSubtitle: 'Build a video training step by step',
    editTitle: 'Edit Training',
    editSubtitle: 'Indication training',
    stepsLabel: 'Training steps',
    steps: {
      basic: 'Basics',
      video: 'Video',
      chapters: 'Chapters',
      quizzes: 'Quizzes',
      settings: 'Audience and Deadline',
      settingsPublish: 'Audience and Publish',
      summary: 'Summary',
    },
    intro: {
      basic: {
        title: 'Introduce the training',
        text: 'Title, description and due date are required. The other fields help employees understand the training.',
        editText: 'Update the title, description and dates. The video can’t be changed when editing.',
      },
      video: {
        title: 'Upload the training video',
        text: 'Choose the video; its length is read automatically and chapters and pop quizzes are placed against it.',
      },
      chapters: {
        title: 'Split the video into chapters',
        text: 'Give each chapter a title and a start / end time. Employees can jump between chapters.',
        editText: 'Update chapter titles and times.',
      },
      quizzes: {
        title: 'Prepare the quizzes',
        text: 'Pop quizzes appear during the video at the moment you set; the final quiz is shown when the video ends and needs a passing score.',
        editText: 'Update the pop quiz and final quiz questions.',
      },
      settings: {
        title: 'Who will see it?',
        text: 'Choose the target groups; the training appears on the profiles of employees in those departments. Optionally set an access deadline, then publish.',
        editText: 'Update the target groups and the access deadline.',
      },
      summary: {
        title: 'Review and save',
        editText: 'Take a last look at your changes and save.',
      },
    },
    title: 'Training title',
    titlePlaceholder: 'e.g. Atopic Dermatitis Indication Training',
    instructor: 'Instructor',
    instructorPlaceholder: 'Instructor’s name',
    category: 'Category',
    categoryPlaceholder: 'e.g. Dermatology',
    description: 'Description',
    descriptionPlaceholder: 'Briefly describe the purpose and scope of the training',
    estimatedDuration: 'Estimated duration',
    estimatedDurationPlaceholder: 'e.g. 45 minutes',
    difficulty: 'Level',
    dueDate: 'Due date',
    dueDateHint: 'Employees are expected to complete the training by this date; their profile reminds them.',
    existingVideo: 'Training video uploaded',
    duration: 'Duration: {{time}}',
    videoLocked: 'The video can’t be changed when editing',
    videoHelp: 'Once the video is chosen its length is read automatically. Upload progress is shown while publishing; keep the window open.',
    pickVideo: 'Choose the training video',
    videoFormats: 'Video files such as MP4, MOV or WebM',
    changeVideo: 'Change video',
    chaptersHelp: 'Enter times as minutes:seconds. Chapters don’t have to cover the whole video.',
    chapterTitle: 'Chapter title',
    chapterTitlePlaceholder: 'e.g. Introduction',
    start: 'Start',
    end: 'End',
    addChapter: 'Add chapter',
    removeChapter: 'Remove chapter',
    popTitle: 'Pop quizzes (during the video)',
    popHelp: 'The video pauses at the moment you set and a question is asked.',
    popItem: 'Pop quiz #{{n}}',
    addPop: 'Add pop quiz',
    showAt: 'Show at (mm:ss)',
    finalTitle: 'Final quiz (after the video)',
    finalHelp: 'Shown after the video is completed; a passing score can be set.',
    finalItem: 'Question #{{n}}',
    addFinal: 'Add final question',
    passScore: 'Passing score (%)',
    passScoreHint: 'Share of correct answers needed to pass the final quiz.',
    question: 'Question',
    questionPlaceholder: 'Type the question',
    optionsHint: 'Options · tap the letter of the correct answer',
    option: 'Option {{letter}}',
    markCorrect: 'Mark option {{letter}} as correct',
    explanation: 'Explanation',
    explanationPlaceholder: 'Optional · shown after answering',
    removeQuestion: 'Remove question',
    targetGroups: 'Target groups',
    targetGroupsHint: 'The training appears on the profiles of employees in these departments.',
    deadlineToggle: 'Set an access deadline',
    deadlineHelp: 'After this date the training is marked “Expired”.',
    deadline: 'Access deadline',
    summary: {
      heading: 'Summary',
      title: 'Title',
      instructor: 'Instructor',
      videoDuration: 'Video length',
      chapters: 'Chapters',
      popQuizzes: 'Pop quizzes',
      finalQuestions: 'Final quiz questions',
      passScore: 'Passing score',
      groups: 'Target groups',
      deadline: 'Access deadline',
    },
    back: 'Back',
    next: 'Next',
    publish: 'Publish Training',
    publishing: 'Publishing...',
    uploading: 'Uploading video... {{percent}}%',
    saveChanges: 'Save Changes',
    toast: {
      created: 'Training created!',
      createError: 'Could not create the training',
      updated: 'Training updated!',
      updateError: 'Could not update the training',
      invalidVideo: 'Please choose a valid video file',
      oneChapter: 'There must be at least one chapter',
    },
  },

  assignments: {
    title: 'Assignments',
    subtitle: 'SOP and training assignments, due dates and completion',
    help: 'All SOP and training assignments are listed with the groups they are assigned to. Those close to their due date are marked amber, overdue ones red.',
    helpManager: 'All SOP and training assignments are listed. Each card shows who has completed it and who is still pending. Those close to their due date are marked amber, overdue ones red.',
    search: 'Search title or group...',
    filterLabel: 'Assignment type',
    filters: {
      all: 'All',
      sop: 'SOP',
      education: 'Training',
    },
    tiles: {
      total: 'Assignments',
      dueSoon: 'Due soon',
      overdue: 'Overdue',
    },
    empty: 'No assignments to show',
    noMatch: 'No assignments match the filter',
    types: {
      sop: 'SOP',
      education: 'Training',
    },
    assignedOn: 'Assigned: {{date}}',
    dueDate: 'Due date',
    deadline: {
      none: 'no due date',
      notSet: 'Not set',
      overdue_one: '{{count}} day overdue',
      overdue_other: '{{count}} days overdue',
      today: 'due today',
      lastDay: 'due tomorrow',
      left_one: '{{count}} day left',
      left_other: '{{count}} days left',
    },
    done_one: '{{count}} completed',
    done_other: '{{count}} completed',
    pending_one: '{{count}} pending',
    pending_other: '{{count}} pending',
    total_one: '/ {{count}} person',
    total_other: '/ {{count}} people',
    doneList_one: 'Completed ({{count}})',
    doneList_other: 'Completed ({{count}})',
    pendingList_one: 'Pending ({{count}})',
    pendingList_other: 'Pending ({{count}})',
    noneDone: 'No one has completed it yet.',
    allDone: 'Everyone has completed it.',
    audience: 'Related staff',
    version: 'Version: {{value}}',
    approval: 'Approved: {{value}}',
    educationDate: 'Training date: {{value}}',
    toast: {
      loadError: 'Could not load assignments',
    },
  },

  questionPool: {
    title: 'Question Pool',
    subtitle: 'Department-based end-of-reading quiz questions',
    rules: 'When an employee finishes reading an SOP or a training, random questions are drawn from their department’s pool and the Common Pool. Inactive questions are never asked.',
    questionCount: 'Questions per quiz',
    questionCountHint: 'Between 1 and 50',
    passScore: 'Passing score (%)',
    passScoreHint: 'Between 1 and 100',
    saveSettings: 'Save settings',
    departments: 'Department pools',
    commonPool: 'Common Pool (All)',
    commonHint: 'These questions can be used in every department’s quizzes.',
    available_one: 'Active questions available for the quiz: {{count}} (including the common pool)',
    available_other: 'Active questions available for the quiz: {{count}} (including the common pool)',
    shortHint_one: 'A quiz needs at least {{count}} active question',
    shortHint_other: 'A quiz needs at least {{count}} active questions',
    activeQuestions: 'Active questions',
    add: 'Add question',
    empty: 'No questions in this pool yet',
    emptyHint: 'Create the first one with “Add question”.',
    loadError: 'Could not load the questions.',
    active: 'Active',
    inactive: 'Inactive',
    onHint: 'Used in quizzes · tap to deactivate',
    offHint: 'Not used in quizzes · tap to activate',
    correct: 'Correct answer',
    deleteTitle: 'Delete question?',
    deleteMessage: 'This question will be removed from the pool. This cannot be undone.',
    newTitle: 'New question',
    editTitle: 'Edit question',
    question: 'Question text',
    questionPlaceholder: 'Type the question',
    optionsHint: 'Options · tap the letter of the correct answer',
    option: 'Option {{letter}}',
    markCorrect: 'Mark option {{letter}} as correct',
    removeOption: 'Remove option {{letter}}',
    addOption: 'Add option',
    explanation: 'Explanation',
    explanationHint: 'Optional · shown on the result screen to employees who pass.',
    explanationPlaceholder: 'Briefly explain why the correct answer is right',
    errors: {
      question: 'Question text is required',
      emptyOption: 'Don’t leave empty options; remove the ones you don’t use',
      duplicate: 'The same option was entered more than once',
    },
    toast: {
      settingsSaved: 'Quiz settings saved',
      settingsError: 'Could not save quiz settings',
      added: 'Question added to the pool',
      updated: 'Question updated',
      saveError: 'Could not save the question',
      deleted: 'Question deleted',
      deleteError: 'Could not delete the question',
      toggleError: 'Could not update the question status',
    },
  },
};

export default quality;
